// TextBlockSection.js — Bloque de texto libre (con imagen opcional a izq/der)
import React from 'react';
import styled, { css } from 'styled-components';
import { buildBg } from '../bgHelpers';
import { BACKEND_BASE } from '../../../services/api';
import { aplicarVariables, tieneVariables, useVariablesUsuario } from '../../../utils/variablesTexto';
import { subrayadoCss } from '../tituloHelpers';

const Wrapper = styled.section`
  padding: ${p => p.$padding || '4rem 2rem'};
  margin: ${p => p.$margin || '0'};
  background: ${p => p.$bg || 'transparent'};
  text-align: ${p => p.$align || 'center'};
  ${p => p.$radius ? `border-radius: ${p.$radius};` : ''}
`;

const Container = styled.div`
  width: ${p => p.$width || '100%'};
  /* Si defines un ancho propio, no lo recortes con el tope por defecto (900px).
     Solo se aplica el tope de 900px cuando no se configuró ningún ancho. */
  max-width: ${p => p.$maxWidth || (p.$width ? 'none' : '900px')};
  margin: 0 auto;
  box-sizing: border-box;
  /* Cuando el fondo se ajusta al ancho del bloque, la caja interior lleva
     fondo + radio + borde + padding (en vez de la sección a pantalla completa). */
  ${p => p.$bg ? `background: ${p.$bg};` : ''}
  ${p => p.$radius ? `border-radius: ${p.$radius}; overflow: hidden;` : ''}
  ${p => p.$border ? `border: ${p.$border};` : ''}
  ${p => p.$pad ? `padding: ${p.$pad};` : ''}
`;

const Title = styled.h2`
  font-size: ${p => p.$size || '38px'};
  color: ${p => p.$color || '#0f355b'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  font-weight: ${p => p.$weight || 'bold'};
  line-height: ${p => p.$lineHeight || '1.2'};
  margin: ${p => p.$margin || '0 0 1.5rem'};
  ${p => p.$subrayado || ''}
`;

const Content = styled.div`
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#333'};
  line-height: ${p => p.$lineHeight || '1.7'};
  font-family: ${p => p.$font || "'Myriad Pro', sans-serif"};
  font-weight: ${p => p.$weight || '400'};
  margin: ${p => p.$margin || '0'};

  /* Evita que enlaces/palabras larguísimas rompan el layout (seguro para todos). */
  overflow-wrap: break-word;

  p { margin-bottom: 1rem; }
  p:last-child { margin-bottom: 0; }
  ul, ol { padding-left: 1.5rem; margin-bottom: 1rem; }
  strong { font-weight: bold; ${p => p.$colorNegritas ? `color: ${p.$colorNegritas};` : ''} }
  a { overflow-wrap: break-word; }

  /* Modo "documento": tipografía cuidada para textos legales largos
     (Términos, Políticas). Opt-in vía config.rich_text — no altera otros bloques. */
  ${p => p.$rich && css`
    text-align: justify;
    hyphens: auto;
    -webkit-hyphens: auto;
    text-justify: inter-word;

    h2, h3, h4 { color: ${p.$colorTitulos || '#0f355b'}; line-height: 1.3; margin: 1.9rem 0 .6rem; text-align: left; }
    h2 { font-size: 1.5em; }
    h3 { font-size: 1.25em; }

    p { margin: 0 0 .95rem; }

    a {
      color: ${p.$colorEnlaces || '#7c3aed'};
      text-decoration: underline;
      word-break: break-all;   /* URLs enormes del documento se parten limpio */
      transition: color .15s;
    }
    a:hover { opacity: .8; }
    u { text-decoration: underline; }

    ol, ul { margin: 1.1rem 0; padding-left: 1.7rem; }
    li { margin-bottom: .75rem; line-height: 1.7; padding-left: .25rem; }
    li::marker { color: ${p.$colorEnlaces || '#7c3aed'}; font-weight: 700; }
    ol > li > strong:first-child { color: ${p.$colorTitulos || '#0f355b'}; }

    /* Normaliza tamaños de fuente sueltos heredados del copy/paste. */
    [style*="font-size: 11px"], [style*="font-size:11px"] { font-size: inherit !important; }

    /* Separadores suaves entre bloques numerados para dar aire. */
    ol { border-top: 1px solid rgba(15,53,91,.08); padding-top: 1.1rem; }
    ol:first-of-type { border-top: 0; padding-top: 0; }
  `}
`;

// Fila texto + imagen (cuando se configura una imagen lateral)
const Row = styled.div`
  display: flex;
  align-items: ${p => p.$valign || 'center'};
  gap: ${p => p.$gap || '2.5rem'};
  flex-direction: ${p => p.$reverse ? 'row-reverse' : 'row'};
  text-align: left;
  @media (max-width: 768px) { flex-direction: column; gap: 1.5rem; }
`;
const ImgCol = styled.div`
  flex: 0 0 ${p => p.$width || '40%'};
  max-width: ${p => p.$width || '40%'};
  @media (max-width: 768px) { flex-basis: auto; max-width: 100%; width: 100%; }
`;
const TextCol = styled.div`flex: 1; min-width: 0;`;
const BlockImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: ${p => p.$radius || '0'};
`;

const resolveImg = (img) => !img ? null : (img.startsWith('http') ? img : `${BACKEND_BASE}/${img}`);

const TextBlockSection = ({ config, tl }) => {
  const align = config.alineacion || 'center';
  // Por defecto el fondo cubre toda la sección (pantalla completa). Si se activa
  // "fondo_ajustado", el fondo (color/imagen) + radio + borde se mueven a la caja
  // interior, que respeta el ancho/ancho máximo configurado.
  const fondoAjustado = config.fondo_ajustado === true;
  const bgValue = buildBg(config, '#fff');

  // Imagen lateral opcional
  const imgUrl = resolveImg(config.imagen);
  const imgDerecha = config.imagen_posicion === 'derecha';

  // Saludos personalizados: {nombre}, {especialidad}… Solo se pide el perfil si
  // el texto realmente trae alguna variable.
  const conVariables = tieneVariables(config.titulo, config.contenido);
  const datosUsuario = useVariablesUsuario(conVariables);
  const texto = (t) => (conVariables ? aplicarVariables(tl(t), datosUsuario) : tl(t));

  const tituloEl = config.titulo && (
    <Title
      $subrayado={subrayadoCss(config)}
      $color={config.color_titulo}
      $size={config.tamano_titulo}
      $weight={config.peso_titulo}
      $font={config.fuente_titulo}
      $lineHeight={config.interlineado_titulo}
      $margin={config.margen_titulo}
      dangerouslySetInnerHTML={{ __html: texto(config.titulo) }}
    />
  );
  const contenidoEl = config.contenido && (
    <Content
      $color={config.color_texto}
      $size={config.tamano_contenido}
      $weight={config.peso_contenido}
      $font={config.fuente_contenido}
      $lineHeight={config.interlineado_contenido}
      $margin={config.margen_contenido}
      $rich={config.rich_text === true || config.tipografia === 'documento'}
      $colorNegritas={config.color_negritas}
      $colorTitulos={config.color_titulos}
      $colorEnlaces={config.color_enlaces}
      dangerouslySetInnerHTML={{ __html: texto(config.contenido) }}
    />
  );

  return (
    <Wrapper
      $bg={fondoAjustado ? 'transparent' : bgValue}
      $align={align}
      $padding={config.padding}
      $margin={config.margen_seccion}
      $radius={fondoAjustado ? undefined : config.radio_fondo}
    >
      <Container
        $width={config.ancho}
        $maxWidth={config.ancho_maximo}
        $bg={fondoAjustado ? bgValue : undefined}
        $radius={fondoAjustado ? config.radio_fondo : undefined}
        $border={fondoAjustado ? config.borde_fondo : undefined}
        $pad={fondoAjustado ? (config.padding_caja || '2.5rem') : undefined}
      >
        {imgUrl ? (
          <Row $reverse={imgDerecha} $gap={config.imagen_gap} $valign={config.imagen_valign}>
            <ImgCol $width={config.imagen_ancho || '40%'}>
              <BlockImg src={imgUrl} alt="" $radius={config.imagen_radio} />
            </ImgCol>
            <TextCol>
              {tituloEl}
              {contenidoEl}
            </TextCol>
          </Row>
        ) : (
          <>
            {tituloEl}
            {contenidoEl}
          </>
        )}
      </Container>
    </Wrapper>
  );
};

export default TextBlockSection;
